/*
 * Shared file for phase lines and event lines.
 * Event lines reach the chart through their own prop rather than `selectedPhaseLines`,
 * because everything that splits the data path reads that one.
 */

const SESSION_X_AXIS_PROPERTIES = ['cumulativeSession', 'cumulativeTrial', 'sessionTrial'];

const LOCAL_SCOPE = 'local';

const LINE_SCOPES = /** @type {const} */ (['global', 'program', 'target', 'subTarget']);

const toTime = (line) => line.startTimestamp.toDate().getTime();

const isInDateRange = (line, { fromTimestamp, toTimestamp }) => (
  typeof line.startTimestamp?.toDate === 'function'
  && line.startTimestamp.toDate() >= fromTimestamp
  && line.startTimestamp.toDate() <= toTimestamp
);

// Mirrors getPhaseLineScopeFilter: a scope without a program or target matches on scope alone.
export const matchesLineScope = ({ scope, program, target }) => (loadedScope) => (
  loadedScope.scope === scope
  && (!program || loadedScope.program === program)
  && (!target || loadedScope.target === target)
);

/**
 * The local lines with a date inside the range, oldest first.
 *
 * @param {Object[] | null | undefined} lines
 * @param {{ fromTimestamp: Date, toTimestamp: Date }} dateRange
 * @returns {Object[]}
 */
export function selectLocalLines(lines, dateRange) {
  return (lines ?? [])
    .filter((line) => (line.scope ?? LOCAL_SCOPE) === LOCAL_SCOPE && isInDateRange(line, dateRange))
    .sort((a, b) => toTime(a) - toTime(b));
}

/**
 * @typedef {{ switchKey: 'isSelected' | 'isEventLinesSelected', linesKey: 'chartPhaseLines' | 'chartEventLines' }}
 *   ScopedLineType
 */

/**
 * A chart keeps one entry per scope in `phaseLines`, with a switch for each line type. A loaded
 * scope holds both types' lines, from one read.
 */
/** @type {ScopedLineType} */
export const SCOPED_PHASE_LINES = { switchKey: 'isSelected', linesKey: 'chartPhaseLines' };
/** @type {ScopedLineType} */
export const SCOPED_EVENT_LINES = { switchKey: 'isEventLinesSelected', linesKey: 'chartEventLines' };

/**
 * Sets one type's switch on a scope entry in a chart's `phaseLines`, keeping the other type's switch.
 *
 * @param {Object[] | null | undefined} scopeEntries A chart's `phaseLines`
 * @param {{ scope: string, program?: string, target?: string }} scopeFilter
 * @param {ScopedLineType} lineType
 * @param {boolean} isSelected
 * @returns {Object[]}
 */
export function setLineScopeSwitch(scopeEntries, scopeFilter, { switchKey }, isSelected) {
  const isScope = matchesLineScope(scopeFilter);
  const entries = scopeEntries ?? [];
  const scopeEntry = entries.find(isScope);

  return [
    ...entries.filter((entry) => !isScope(entry)),
    { ...scopeEntry, ...scopeFilter, [switchKey]: isSelected },
  ];
}

/**
 * The lines of one type from the scopes switched on for it, inside the date range.
 *
 * @param {Object[] | null | undefined} scopeEntries A chart's `phaseLines`, which holds its scope entries
 * @param {Object[]} loadedScopes
 * @param {ScopedLineType} lineType
 * @param {{ fromTimestamp: Date, toTimestamp: Date }} dateRange
 * @returns {Object[]}
 */
export function selectScopedLines(scopeEntries, loadedScopes, { switchKey, linesKey }, dateRange) {
  return (scopeEntries ?? [])
    .filter((entry) => LINE_SCOPES.includes(entry.scope) && entry[switchKey] === true)
    .flatMap((entry) => loadedScopes.find(matchesLineScope(entry))?.[linesKey] ?? [])
    .filter((line) => isInDateRange(line, dateRange));
}

/**
 * On a session x-axis a line sits on the first session after it, or after the last visible session
 * when none follows.
 *
 * @param {Object[]} lines
 * @param {Object[]} chartData
 * @param {string} xAxisDataProperty
 * @returns {Object[]}
 */
export function placeLinesOnSessions(lines, chartData, xAxisDataProperty) {
  if (!SESSION_X_AXIS_PROPERTIES.includes(xAxisDataProperty)) {
    return lines;
  }

  const lastSession = chartData
    .filter((data) => (
      data.hidden !== true && data[xAxisDataProperty] != null && !Number.isNaN(data[xAxisDataProperty])
    ))
    .reduce((last, data) => (last === null || data[xAxisDataProperty] > last[xAxisDataProperty] ? data : last), null);

  return lines.map((line) => {
    const session = chartData.find((data) => (
      data[xAxisDataProperty] && data.sessionDate && data.date.getTime() > toTime(line)
    ));
    if (!session) {
      return { ...line, category: lastSession?.sessionDate, isAfterData: true };
    }
    return { ...line, category: session.sessionDate };
  });
}

/**
 * Applies what a line dialog returned: a line marked `editStatus: 'delete'` is removed, any other
 * replaces the line with its id or is added.
 *
 * @param {Object[] | undefined} lines
 * @param {Object} changedLine
 * @returns {Object[]}
 */
export function applyLineChange(lines, changedLine) {
  const others = (lines ?? []).filter(({ id }) => id !== changedLine.id);
  return changedLine.editStatus === 'delete' ? others : [...others, changedLine];
}
